// Voice-note limits. The server caps audio at 25 MB (contract: evidence/sign); two minutes is the
// product cap — long enough to describe what happened, short enough to upload on one bar.
import { formatBytes } from '../helper';

export const MAX_VOICE_SEC = 120;
export const MAX_VOICE_BYTES = 25 * 1024 * 1024;
/** Shorter than this is an accidental tap, not a note. */
export const MIN_VOICE_SEC = 1;

export interface IVoiceNote {
	blob: Blob;
	durationSec: number;
	mime: string;
}

export const validateVoiceNote = (note: {
	bytes: number;
	durationSec: number;
	mime: string;
}): { ok: true } | { ok: false; error: string } => {
	if (!note.mime.startsWith('audio/')) return { ok: false, error: 'That recording isn’t audio. Record it again.' };
	if (!Number.isFinite(note.durationSec) || note.durationSec < MIN_VOICE_SEC) {
		return { ok: false, error: 'Too short — hold the button while you speak.' };
	}
	if (note.durationSec > MAX_VOICE_SEC + 1) return { ok: false, error: 'Voice notes stop at 2 minutes.' };
	if (note.bytes > MAX_VOICE_BYTES) return { ok: false, error: `Voice note must be ${formatBytes(MAX_VOICE_BYTES)} or less.` };
	if (note.bytes <= 0) return { ok: false, error: 'Nothing was recorded. Check the microphone and try again.' };
	return { ok: true };
};

/** First container the browser can record — Safari records mp4, Chrome/Android webm/opus. */
export const pickAudioMimeType = (): string => {
	if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
	const candidates = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/webm'];
	return candidates.find((t) => MediaRecorder.isTypeSupported(t)) ?? '';
};

export const formatClock = (seconds: number) => {
	const s = Math.max(0, Math.floor(seconds));
	return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
